//
// Copyright (c) 2026 Comcast
//

import styled from 'styled-components'
import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useSearchParams, createSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import useOnclickOutside from 'react-cool-onclickoutside'

import * as sessionUserSelector from 'selectors/sessionUser'
import * as ticketingSystemsSelector from 'selectors/ticketingSystems'

import {
  RouteTicketsSearchResults,
  RouteTicketProviderTicketsSearchResults,
  RouteTicketingSystemsTicket,
  RouteTicketProviderTicket,
} from 'routes'

import { quickSearch } from 'api/tickets'
import { quickSearchProviderTickets } from 'api/ticketProviderTickets'
import { useFetchTicketProvidersQuery } from 'api/ticketProviders'

import Search from 'components/Shared/Search/Index'
import SearchDropdown from './Dropdown'

const MIN_QUERY_LENGTH = 3
const DEBOUNCE_DELAY_MS = 500
const MAX_TICKETS_PER_SYSTEM = 3

const StyledWrapper = styled.div`
  height: 24px;
  margin-right: 8px;
`

const GlobalSearch = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()

  const isUserPrivileged = useSelector(sessionUserSelector.isUserPrivileged)
  const ticketingSystems = useSelector(ticketingSystemsSelector.ticketingSystems)

  const { data: ticketProvidersData } = useFetchTicketProvidersQuery(undefined, {
    skip: !isUserPrivileged,
  })
  const ticketProviders = useMemo(
    () => ticketProvidersData?.ticketProviders || [],
    [ticketProvidersData],
  )

  const quickSearchEndpoint = isUserPrivileged ? quickSearchProviderTickets : quickSearch

  const searchResultsPath = isUserPrivileged
    ? RouteTicketProviderTicketsSearchResults()
    : RouteTicketsSearchResults()

  const isSearchResultsPage = location.pathname === searchResultsPath
  const initialValue = isSearchResultsPage ? (searchParams.get('search') || '') : ''

  const [isLoading, setIsLoading] = useState(false)
  const [searchResults, setSearchResults] = useState(null)
  const [currentSearchValue, setCurrentSearchValue] = useState('')
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const [isDropdownOpen, setIsDropdownOpen] = useState(true)

  const debouncedTimerRef = useRef(null)
  const searchInputRef = useRef(null)
  const latestSearchValueRef = useRef('')

  const focusInput = useCallback(() => {
    setFocusedIndex(-1)
    searchInputRef.current?.focus()
  }, [])

  const processResults = useCallback((allResults, searchValue) => {
    const groups = []

    allResults.forEach(result => {
      if (result.tickets && result.tickets.length > 0) {
        groups.push({
          name: result.name,
          tickets: result.tickets.map(ticket => ({
            ...ticket,
            ticketingSystemDisplayId: result.ticketingSystemDisplayId,
            ticketProviderId: result.ticketProviderId,
          })),
        })
      }
    })

    if (groups.length === 0) {
      return { groups: [], resultsLabel: null }
    }

    const allTickets = groups.flatMap(g => g.tickets)
    const exactMatch = allTickets.find(ticket =>
      ticket.number === searchValue || ticket.id === searchValue
    )

    if (exactMatch && allTickets.length === 1) {
      return {
        groups: [{ name: groups[0]?.name, tickets: [exactMatch] }],
        resultsLabel: t('mainHeader.globalSearch.exactMatch'),
        isExactMatch: true,
      }
    }

    return {
      groups,
      resultsLabel: t('mainHeader.globalSearch.suggested'),
      isExactMatch: false,
    }
  }, [t])

  const performQuickSearch = useCallback(async (value) => {
    if (value.length < MIN_QUERY_LENGTH) {
      setSearchResults(null)
      setIsLoading(false)
      return
    }

    const sources = isUserPrivileged ? ticketProviders : ticketingSystems

    if (sources.length === 0) {
      setSearchResults({ groups: [], resultsLabel: null })
      setIsLoading(false)
      return
    }

    try {
      const promises = sources.map(async (source) => {
        const sourceId = isUserPrivileged ? source.id : source.displayId

        try {
          const params = isUserPrivileged
            ? { ticketProviderId: sourceId, searchTerm: value, perPage: MAX_TICKETS_PER_SYSTEM }
            : { ticketingSystemDisplayId: sourceId, searchTerm: value, perPage: MAX_TICKETS_PER_SYSTEM }

          // subscribe: false keeps the parallel requests independent — a shared useLazyQuery
          // trigger unsubscribes the previous call, and RTKQ >= 2.9.0 aborts unsubscribed requests
          const data = await dispatch(
            quickSearchEndpoint.initiate(params, { subscribe: false, forceRefetch: true }),
          ).unwrap()

          return {
            ticketingSystemDisplayId: isUserPrivileged ? null : sourceId,
            ticketProviderId: isUserPrivileged ? sourceId : null,
            name: source.name,
            tickets: data?.tickets || [],
          }
        } catch (error) {
          console.error(`Error fetching tickets for ${sourceId}:`, error)
          return {
            ticketingSystemDisplayId: isUserPrivileged ? null : sourceId,
            ticketProviderId: isUserPrivileged ? sourceId : null,
            name: source.name,
            tickets: [],
          }
        }
      })

      const results = await Promise.all(promises)

      // Discard results if input has changed since this search started
      if (value !== latestSearchValueRef.current) {
        return
      }

      setSearchResults(processResults(results, value))
      focusInput()
    } catch (error) {
      console.error('Search error:', error)
      if (value === latestSearchValueRef.current) {
        setSearchResults({ groups: [], resultsLabel: null })
        focusInput()
      }
    } finally {
      if (value === latestSearchValueRef.current) {
        setIsLoading(false)
      }
    }
  }, [isUserPrivileged, ticketProviders, ticketingSystems, quickSearchEndpoint, dispatch, processResults, focusInput])

  useEffect(() => {
    return () => clearTimeout(debouncedTimerRef.current)
  }, [performQuickSearch])

  const onInputChange = useCallback((value) => {
    latestSearchValueRef.current = value
    setCurrentSearchValue(value)
    setFocusedIndex(-1)
    setIsDropdownOpen(true)
    clearTimeout(debouncedTimerRef.current)

    if (value.length >= MIN_QUERY_LENGTH) {
      // setTimeout/clearTimeout instead of lodash debounce — single ref, no stale
      // instances when performQuickSearch is recreated between renders
      debouncedTimerRef.current = setTimeout(() => {
        setIsLoading(true)
        performQuickSearch(value)
      }, DEBOUNCE_DELAY_MS)
    } else {
      setIsLoading(false)
      setSearchResults(null)
    }
  }, [performQuickSearch])

  const onSubmit = useCallback((value) => {
    if (value) {
      setSearchResults(null)
      setFocusedIndex(-1)
      navigate({
        pathname: searchResultsPath,
        search: `?${createSearchParams({ search: value })}`,
      })
    } else if (initialValue) {
      navigate({ pathname: '/' })
    }
  }, [navigate, searchResultsPath, initialValue])

  const buildTicketUrl = useCallback((ticket) => {
    if (ticket.ticketProviderId) {
      return RouteTicketProviderTicket({
        ticketProviderId: ticket.ticketProviderId,
        ticketId: ticket.number || ticket.id,
      })
    }
    return RouteTicketingSystemsTicket({
      ticketingSystemDisplayId: ticket.ticketingSystemDisplayId,
      ticketId: ticket.number || ticket.id,
    })
  }, [])

  const onLinkClick = useCallback(() => {
    setSearchResults(null)
    setFocusedIndex(-1)
  }, [])

  const onClickOutside = useCallback(() => {
    setIsDropdownOpen(false)
    setFocusedIndex(-1)
  }, [])

  const dropdownRef = useOnclickOutside(onClickOutside)

  const showDropdown = (isLoading || searchResults) && currentSearchValue.length >= MIN_QUERY_LENGTH && isDropdownOpen

  const ticketCount = searchResults?.groups?.reduce((sum, g) => sum + g.tickets.length, 0) || 0
  // tickets (during loading) + 'View all' link
  const totalItems = isLoading ? 1 : ticketCount + 1

  const onKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()

      // Reopen dropdown with last results if closed
      if (!isDropdownOpen && searchResults) {
        setIsDropdownOpen(true)
        setFocusedIndex(0)
        return
      }

      if (showDropdown) {
        setFocusedIndex(prev => prev >= totalItems - 1 ? 0 : prev + 1)
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()

      // Reopen dropdown with last results if closed, focus last item
      if (!isDropdownOpen && searchResults) {
        setIsDropdownOpen(true)
        setFocusedIndex(totalItems - 1)
        return
      }

      if (showDropdown && focusedIndex > 0) {
        setFocusedIndex(prev => prev - 1)
      } else if (focusedIndex === 0) {
        // Return focus to input
        focusInput()
      } else if (focusedIndex === -1 && showDropdown) {
        // From input field, go to last item
        setFocusedIndex(totalItems - 1)
      }
    } else if (e.key === 'ArrowLeft' && focusedIndex >= 0) {
      // Return focus to input, keep dropdown open
      focusInput()
    } else if (e.key === 'Escape') {
      if (showDropdown) {
        // First ESC closes dropdown
        e.preventDefault()
        e.stopPropagation()
        setIsDropdownOpen(false)
        focusInput()
      }
      // Second ESC will be handled by Search component to close the search field
    }
  }, [isDropdownOpen, focusedIndex, totalItems, searchResults, focusInput, showDropdown])

  const viewAllUrl = `${searchResultsPath}?${createSearchParams({ search: currentSearchValue })}`

  const renderDropdown = useCallback(({ isVisible }) => {
    if (!isVisible || !showDropdown) {
      return null
    }

    return (
      <div ref={dropdownRef} data-test='global-search-dropdown'>
        <SearchDropdown
          isLoading={isLoading}
          searchResults={searchResults}
          viewAllUrl={viewAllUrl}
          buildTicketUrl={buildTicketUrl}
          onLinkClick={onLinkClick}
          focusedIndex={focusedIndex}
          searchValue={searchResults?.isExactMatch ? '' : currentSearchValue}
        />
      </div>
    )
  }, [showDropdown, isLoading, searchResults, viewAllUrl, buildTicketUrl, onLinkClick, dropdownRef, focusedIndex, currentSearchValue])

  return (
    <StyledWrapper onKeyDown={onKeyDown} data-test='global-search'>
      <Search
        key={initialValue}
        initialValue={initialValue}
        onChange={onSubmit}
        onInputChange={onInputChange}
        placeholder={t('mainHeader.globalSearch.placeholder')}
        withHotKeys
        renderDropdown={renderDropdown}
        inputRef={searchInputRef}
      />
    </StyledWrapper>
  )
}

export default GlobalSearch
